import { useEffect } from "react";

const SHORTCUT_SUPPRESSED_SELECTOR = ".ant-modal, [role='dialog']";

type SaveProjectShortcutHandlerOptions = {
  canSave: boolean;
  isSaving: boolean;
  isPaused?: boolean;
  onSave: () => void | Promise<void>;
};

function isPrimaryShortcutModifier(
  e: Pick<KeyboardEvent, "metaKey" | "ctrlKey">
): boolean {
  return Boolean(e.metaKey || e.ctrlKey);
}

function getShortcutKey(e: Pick<KeyboardEvent, "key">): string {
  return e.key.toLowerCase();
}

export function isSaveProjectShortcut(
  e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">
): boolean {
  return (
    isPrimaryShortcutModifier(e) &&
    !e.altKey &&
    !e.shiftKey &&
    getShortcutKey(e) === "s"
  );
}

export function isSaveProjectShortcutSuppressedTarget(
  target: EventTarget | null
): boolean {
  if (!target || typeof target !== "object") {
    return false;
  }

  const element = target as HTMLElement;
  return Boolean(element.closest?.(SHORTCUT_SUPPRESSED_SELECTOR));
}

export function handleSaveProjectShortcut(
  e: KeyboardEvent,
  options: SaveProjectShortcutHandlerOptions
): void {
  if (!isSaveProjectShortcut(e)) {
    return;
  }

  e.preventDefault();

  if (
    e.repeat ||
    options.isPaused ||
    options.isSaving ||
    !options.canSave ||
    isSaveProjectShortcutSuppressedTarget(e.target)
  ) {
    return;
  }

  void options.onSave();
}

export function useSaveProjectShortcut(
  options: SaveProjectShortcutHandlerOptions
): void {
  const { canSave, isPaused, isSaving, onSave } = options;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) =>
      handleSaveProjectShortcut(e, { canSave, isPaused, isSaving, onSave });

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [canSave, isPaused, isSaving, onSave]);
}
